import type { CSSProperties } from 'react';
import { minTouchTarget, semanticColor, space, typeStyle } from '@beautyburst/tokens';
import type { RetailerActor } from './App.tsx';
import type { RetailerNavKey, RouteContract } from './routes.ts';
import { retailerHref } from './base-path.ts';

const focusClass = 'bb-retailer-nav-focus';

const baseLink: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  minWidth: minTouchTarget,
  minHeight: minTouchTarget,
  padding: `0 ${space[3]}`,
  color: semanticColor.textLink,
  textDecoration: 'none',
  ...typeStyle('label'),
};

function FocusStyles() {
  return (
    <style>{`.${focusClass}:focus-visible{outline:3px solid ${semanticColor.focusRing};outline-offset:2px}`}</style>
  );
}

export function allowedNavRoutes(
  routes: readonly RouteContract[],
  flags: ReadonlySet<string>,
  actor: RetailerActor | null,
): readonly RouteContract[] {
  const keys = new Set<RetailerNavKey>([
    'dashboard',
    'orders',
    'catalog',
    'inventory',
    'imports',
    'store',
    'after-hours',
    'payouts',
    'security',
  ]);
  return routes.filter(
    (route) =>
      route.navKey !== null &&
      keys.has(route.navKey) &&
      !route.path.includes(':') &&
      route.available &&
      flags.has(route.flag) &&
      (route.permission === null || actor?.permissions.has(route.permission) === true),
  );
}

export function DesktopNav({
  activeNavKey,
  routes,
}: {
  activeNavKey: RetailerNavKey;
  routes: readonly RouteContract[];
}) {
  if (routes.length === 0) return null;
  return (
    <nav aria-label="Retailer primary" style={{ display: 'flex', gap: space[1], flexWrap: 'wrap' }}>
      <FocusStyles />
      {routes.map((route) => {
        const active = activeNavKey === route.navKey;
        return (
          <a
            key={route.path}
            className={focusClass}
            href={retailerHref(route.path)}
            aria-current={active ? 'page' : undefined}
            style={{
              ...baseLink,
              color: active ? semanticColor.brandContrast : semanticColor.textLink,
              fontWeight: active ? 700 : undefined,
            }}
          >
            {route.screen}
          </a>
        );
      })}
    </nav>
  );
}

export function MobileBottomNav({
  activeNavKey,
  routes,
}: {
  activeNavKey: RetailerNavKey;
  routes: readonly RouteContract[];
}) {
  if (routes.length === 0) return null;
  return (
    <nav
      aria-label="Retailer mobile"
      style={{
        position: 'sticky',
        bottom: 0,
        display: 'flex',
        overflowX: 'auto',
        gap: space[1],
        background: semanticColor.surfaceCard,
        borderTop: `1px solid ${semanticColor.borderDefault}`,
        padding: `${space[2]} ${space[3]}`,
      }}
    >
      <FocusStyles />
      {routes.map((route) => {
        const active = activeNavKey === route.navKey;
        return (
          <a
            key={route.path}
            className={focusClass}
            href={retailerHref(route.path)}
            aria-current={active ? 'page' : undefined}
            style={{
              ...baseLink,
              whiteSpace: 'nowrap',
              color: active ? semanticColor.brandContrast : semanticColor.textLink,
              fontWeight: active ? 700 : undefined,
            }}
          >
            {route.screen}
          </a>
        );
      })}
    </nav>
  );
}
